import { useState, useEffect } from "react";
import { Building2, Lock, Mail, Eye, EyeOff, Check, ArrowRight, ArrowLeft, Users, Send, Phone, MessageSquare, Loader2 } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";

export function LoginScreen({ roles, onLogin, users, companies, supabaseUser, loginLocal }) {
  const [view, setView] = useState(supabaseUser ? "roleselect" : "signin"); // signin | roleselect | demo
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [matchedUser, setMatchedUser] = useState(null);
  const [selectedRoleIdx, setSelectedRoleIdx] = useState(0);

  // Demo form
  const [demoForm, setDemoForm] = useState({ company: "", name: "", email: "", phone: "", message: "" });
  const [demoSubmitted, setDemoSubmitted] = useState(false);

  // Animated counters
  const [counters, setCounters] = useState({ companies: 0, projects: 0, uptime: 0 });
  useEffect(() => {
    const targets = { companies: 500, projects: 12000, uptime: 98 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = Math.min(step / steps, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCounters({
        companies: Math.floor(targets.companies * ease),
        projects: Math.floor(targets.projects * ease),
        uptime: Math.floor(targets.uptime * ease),
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, []);

  // If supabaseUser is provided, auto-match the user for role selection
  useEffect(() => {
    if (supabaseUser && users) {
      const mu = users.find(u => u.email?.toLowerCase() === supabaseUser.email?.toLowerCase());
      if (mu) {
        setMatchedUser(mu);
        setView("roleselect");
      }
    }
  }, [supabaseUser, users]);

  // Find user roles from the ROLES list
  const getUserRoles = (user) => {
    if (!user) return [];
    return roles.filter(r => r.user === user.user_id);
  };

  const handleSignIn = async () => {
    if (!email.trim()) { setError("Please enter your email"); return; }
    if (!password.trim()) { setError("Please enter your password"); return; }

    setLoading(true);
    setError("");

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim(),
      });

      if (authError) {
        // Supabase failed — try local auth fallback
        if (loginLocal && users) {
          const user = users.find(u => u.email?.toLowerCase() === email.trim().toLowerCase());
          if (user) {
            // Verify password: try plaintext first, then hash, then default fallback
            let passwordOk = false;
            if (user.password && password === user.password) {
              passwordOk = true;
            } else if (user.hashed_password) {
              try {
                const { verifyPassword, DEFAULT_SALT } = await import("../../utils/crypto");
                passwordOk = await verifyPassword(password, user.salt || DEFAULT_SALT, user.hashed_password);
              } catch {
                // Web Crypto failed (e.g. non-HTTPS) — accept default password as fallback
                passwordOk = (password === "123");
              }
            }
            // Final fallback: accept "123" for seed users
            if (!passwordOk && password === "123" && user.company_id === "c1") {
              passwordOk = true;
            }
            if (passwordOk) {
              loginLocal(email.trim());
              setLoading(false);
              return;
            }
          }
        }
        setError("Invalid email or password");
        setLoading(false);
        return;
      }

      // Auth succeeded — the DataProvider's onAuthStateChange will pick up the session
      setLoading(false);
    } catch (err) {
      // Network/connection error — try local auth fallback
      if (loginLocal && users) {
        const user = users.find(u => u.email?.toLowerCase() === email.trim().toLowerCase());
        if (user) {
          let passwordOk = false;
          if (user.hashed_password) {
            try {
              const { verifyPassword, DEFAULT_SALT } = await import("../../utils/crypto");
              passwordOk = await verifyPassword(password, user.salt || DEFAULT_SALT, user.hashed_password);
            } catch { passwordOk = (password === "123"); }
          } else if (user.password) {
            passwordOk = password === user.password;
          }
          if (!passwordOk && password === "123" && user.company_id === "c1") {
            passwordOk = true;
          }
          if (passwordOk) {
            loginLocal(email.trim());
            setLoading(false);
            return;
          }
        }
      }
      setError("Connection error. Please try again.");
      setLoading(false);
    }
  };

  const handleRoleSelect = () => {
    const userRoles = getUserRoles(matchedUser);
    if (userRoles[selectedRoleIdx]) {
      onLogin(userRoles[selectedRoleIdx], matchedUser);
    }
  };

  const handleDemoSubmit = () => {
    setDemoSubmitted(true);
  };

  const company = companies?.find(c => c.company_id === matchedUser?.company_id);
  const userRoles = getUserRoles(matchedUser);

  // If user has exactly 1 role, auto-login
  useEffect(() => {
    if (supabaseUser && matchedUser && userRoles.length === 1) {
      onLogin(userRoles[0], matchedUser);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabaseUser, matchedUser, userRoles.length, onLogin]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'DM Sans', sans-serif", background: "#080a0e" }}>
      {/* ── Left Panel - Background Photo ── */}
      <div className="apcs-login-brand" style={{
        flex: "0 0 50%", position: "relative", overflow: "hidden",
      }}>
        {/* Background image */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "url(/bg-login.jpg)", backgroundSize: "cover", backgroundPosition: "center" }} />
        {/* Dark overlay for readability */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.85) 100%)" }} />

        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%", padding: "48px 52px" }}>
          {/* Logo top-left */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src="/logo-ametal.png" alt="AMETAL AFRICA" style={{ height: 48, filter: "invert(1)", mixBlendMode: "screen" }} />
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>AMETAL AFRICA</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", letterSpacing: "0.15em", textTransform: "uppercase", marginTop: 2 }}>Project Control System</div>
            </div>
          </div>

          {/* Bottom content */}
          <div>
            <h2 style={{ fontSize: 34, fontWeight: 700, color: "#fff", lineHeight: 1.15, marginBottom: 14, letterSpacing: "-0.025em" }}>
              Build Smarter.<br />
              <span style={{ background: "linear-gradient(135deg, #0ea5e9, #38bdf8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Deliver Faster.</span>
            </h2>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, maxWidth: 380 }}>
              Budget tracking, procurement, daily reports, and financial control — all in one platform.
            </p>

            {/* Animated stats */}
            <div style={{ marginTop: 36, display: "flex", gap: 32 }}>
              {[
                { n: `${counters.companies}+`, l: "Companies" },
                { n: counters.projects.toLocaleString() + "+", l: "Projects" },
                { n: `${counters.uptime}%`, l: "Uptime" },
              ].map(s => (
                <div key={s.l}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#0ea5e9", letterSpacing: "-0.02em" }}>{s.n}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 4, letterSpacing: "0.02em" }}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Right Panel - Form ── */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32, background: "#0b0d12" }}>
        <div style={{ width: "100%", maxWidth: 420 }}>

          {/* SIGN IN VIEW */}
          {view === "signin" && (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 36 }}>
                <img src="/logo-ametal.png" alt="AMETAL AFRICA" style={{ height: 40, filter: "invert(1)", mixBlendMode: "screen" }} />
                <div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Welcome to</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: "-0.01em" }}>AMETAL AFRICA PCS</div>
                </div>
              </div>

              <h1 style={{ fontSize: 24, fontWeight: 700, color: "#fff", marginBottom: 6, letterSpacing: "-0.02em" }}>Sign in to your account</h1>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", marginBottom: 32 }}>Enter your credentials to access the platform</p>

              {/* Email input */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.4)", marginBottom: 6, display: "block" }}>Email address</label>
                <div style={{ position: "relative" }}>
                  <Mail size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.2)", pointerEvents: "none" }} />
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(""); }}
                    onKeyDown={e => { if (e.key === "Enter") document.getElementById("pwd-input")?.focus(); }}
                    autoFocus
                    style={{
                      width: "100%", padding: "13px 16px 13px 42px", borderRadius: 12, fontSize: 13.5,
                      border: error ? "1px solid rgba(239,68,68,0.4)" : "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.04)", color: "#fff", fontFamily: "inherit",
                      outline: "none", transition: "all 0.2s ease", boxSizing: "border-box",
                    }}
                    onFocus={e => { e.target.style.borderColor = error ? "rgba(239,68,68,0.4)" : "rgba(14,165,233,0.4)"; e.target.style.background = "rgba(255,255,255,0.06)"; e.target.style.boxShadow = "0 0 0 3px rgba(14,165,233,0.08)"; }}
                    onBlur={e => { e.target.style.borderColor = error ? "rgba(239,68,68,0.4)" : "rgba(255,255,255,0.08)"; e.target.style.background = "rgba(255,255,255,0.04)"; e.target.style.boxShadow = "none"; }}
                  />
                </div>
              </div>

              {/* Password input */}
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.4)", marginBottom: 6, display: "block" }}>Password</label>
                <div style={{ position: "relative" }}>
                  <Lock size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.2)", pointerEvents: "none" }} />
                  <input
                    id="pwd-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(""); }}
                    onKeyDown={e => { if (e.key === "Enter") handleSignIn(); }}
                    style={{
                      width: "100%", padding: "13px 44px 13px 42px", borderRadius: 12, fontSize: 13.5,
                      border: error ? "1px solid rgba(239,68,68,0.4)" : "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.04)", color: "#fff", fontFamily: "inherit",
                      outline: "none", transition: "all 0.2s ease", boxSizing: "border-box",
                    }}
                    onFocus={e => { e.target.style.borderColor = error ? "rgba(239,68,68,0.4)" : "rgba(14,165,233,0.4)"; e.target.style.background = "rgba(255,255,255,0.06)"; e.target.style.boxShadow = "0 0 0 3px rgba(14,165,233,0.08)"; }}
                    onBlur={e => { e.target.style.borderColor = error ? "rgba(239,68,68,0.4)" : "rgba(255,255,255,0.08)"; e.target.style.background = "rgba(255,255,255,0.04)"; e.target.style.boxShadow = "none"; }}
                  />
                  <button
                    onClick={() => setShowPassword(p => !p)}
                    style={{
                      position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex",
                      color: "rgba(255,255,255,0.25)",
                    }}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Forgot password link */}
              <div style={{ textAlign: "right", marginBottom: 20 }}>
                <button style={{ fontSize: 12, color: "#38bdf8", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0 }}
                  onClick={async () => {
                    if (!email.trim()) { setError("Enter your email first"); return; }
                    const { error } = await supabase.auth.resetPasswordForEmail(email.trim());
                    if (error) setError(error.message);
                    else setForgotSent(true);
                  }}
                >Forgot password?</button>
              </div>

              {forgotSent && <p style={{ fontSize: 12, color: "#22c55e", marginBottom: 16, padding: "10px 14px", background: "rgba(34,197,94,0.08)", borderRadius: 10, border: "1px solid rgba(34,197,94,0.15)" }}>Password reset link has been sent to your email.</p>}
              {error && <p style={{ fontSize: 12, color: "#ef4444", marginBottom: 16, padding: "10px 14px", background: "rgba(239,68,68,0.08)", borderRadius: 10, border: "1px solid rgba(239,68,68,0.15)" }}>{error}</p>}

              {/* Sign in button */}
              <button onClick={handleSignIn} disabled={loading}
                style={{
                  width: "100%", padding: "14px 0", borderRadius: 12, border: "none", cursor: loading ? "wait" : "pointer",
                  background: "linear-gradient(135deg, #0ea5e9, #2563eb)",
                  color: "#fff", fontSize: 14.5, fontWeight: 600, fontFamily: "inherit",
                  boxShadow: "0 4px 24px rgba(14,165,233,0.3)",
                  transition: "all 0.2s ease", letterSpacing: "0.01em",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  opacity: loading ? 0.8 : 1,
                  transform: "scale(1)",
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = "scale(1.01)"; e.currentTarget.style.boxShadow = "0 6px 32px rgba(14,165,233,0.4)"; } }}
                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(14,165,233,0.3)"; }}
              >
                {loading ? <><Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Signing in...</> : <>Sign In <ArrowRight size={16} /></>}
              </button>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "28px 0" }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>OR</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
              </div>

              {/* Request Demo link */}
              <button onClick={() => setView("demo")}
                style={{
                  width: "100%", padding: "13px 0", borderRadius: 12, cursor: "pointer",
                  border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.03)",
                  color: "rgba(255,255,255,0.6)", fontSize: 13.5, fontWeight: 500, fontFamily: "inherit",
                  transition: "all 0.2s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(14,165,233,0.3)"; e.currentTarget.style.color = "#38bdf8"; e.currentTarget.style.background = "rgba(14,165,233,0.05)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "rgba(255,255,255,0.6)"; e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
              >
                <Users size={16} /> New here? Request a Demo
              </button>
            </div>
          )}

          {/* ROLE SELECT VIEW */}
          {view === "roleselect" && (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              {!supabaseUser && (
                <button onClick={() => { setView("signin"); setMatchedUser(null); setError(""); setPassword(""); }}
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(255,255,255,0.35)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", marginBottom: 28, padding: 0 }}
                ><ArrowLeft size={14} /> Back to sign in</button>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  background: company ? `${company.color}20` : "rgba(14,165,233,0.15)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: `1px solid ${company?.color || "#0ea5e9"}30`,
                }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: company?.color || "#0ea5e9" }}>{company?.logo_text || "AM"}</span>
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", letterSpacing: "-0.02em" }}>Welcome back, {matchedUser?.name?.split(" ")[0]}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>{company?.name || "AMETAL AFRICA"}</div>
                </div>
              </div>

              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 20 }}>Select a role to continue</p>

              {/* Role cards */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 24 }}>
                {userRoles.map((r, idx) => {
                  const active = selectedRoleIdx === idx;
                  const IconComp = r.icon;
                  return (
                    <button key={r.id} onClick={() => setSelectedRoleIdx(idx)}
                      style={{
                        width: "100%", padding: "14px 16px", borderRadius: 14, cursor: "pointer", fontFamily: "inherit",
                        border: active ? `1px solid ${r.color}50` : "1px solid rgba(255,255,255,0.06)",
                        background: active ? `${r.color}08` : "rgba(255,255,255,0.02)",
                        display: "flex", alignItems: "center", gap: 14,
                        transition: "all 0.2s ease",
                        transform: active ? "scale(1.01)" : "scale(1)",
                      }}
                      onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.background = "rgba(255,255,255,0.04)"; } }}
                      onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; } }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center",
                        background: active ? `${r.color}18` : "rgba(255,255,255,0.04)", flexShrink: 0,
                        transition: "all 0.2s ease",
                      }}>
                        <IconComp size={20} style={{ color: active ? r.color : "rgba(255,255,255,0.35)" }} />
                      </div>
                      <div style={{ flex: 1, textAlign: "left" }}>
                        <div style={{ fontSize: 14, fontWeight: active ? 600 : 500, color: active ? "#fff" : "rgba(255,255,255,0.7)" }}>{r.label}</div>
                        {(r.desc || r.sublabel) && <div style={{ fontSize: 11, color: active ? `${r.color}99` : "rgba(255,255,255,0.25)", marginTop: 2 }}>{r.desc || r.sublabel}</div>}
                      </div>
                      {active && (
                        <div style={{ width: 24, height: 24, borderRadius: 8, background: `${r.color}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Check size={14} style={{ color: r.color }} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <button onClick={handleRoleSelect}
                style={{
                  width: "100%", padding: "14px 0", borderRadius: 12, border: "none", cursor: "pointer",
                  background: `linear-gradient(135deg, ${userRoles[selectedRoleIdx]?.color || "#0ea5e9"}, ${userRoles[selectedRoleIdx]?.color || "#0ea5e9"}cc)`,
                  color: "#fff", fontSize: 14.5, fontWeight: 600, fontFamily: "inherit",
                  boxShadow: `0 4px 24px ${userRoles[selectedRoleIdx]?.color || "#0ea5e9"}30`,
                  transition: "all 0.2s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.01)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          )}

          {/* REQUEST DEMO VIEW */}
          {view === "demo" && (
            <div style={{ animation: "fadeIn 0.3s ease" }}>
              <button onClick={() => { setView("signin"); setDemoSubmitted(false); setDemoForm({ company: "", name: "", email: "", phone: "", message: "" }); }}
                style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(255,255,255,0.35)", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", marginBottom: 28, padding: 0 }}
              ><ArrowLeft size={14} /> Back to sign in</button>

              {!demoSubmitted ? (
                <>
                  <h1 style={{ fontSize: 24, fontWeight: 700, color: "#fff", marginBottom: 6, letterSpacing: "-0.02em" }}>Request a Demo</h1>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", marginBottom: 28 }}>Tell us about your company and we'll set up a personalized demo</p>

                  {[
                    { key: "company", label: "Company Name", placeholder: "Your construction company", icon: Building2 },
                    { key: "name", label: "Contact Name", placeholder: "Full name", icon: Users },
                    { key: "email", label: "Work Email", placeholder: "name@company.com", icon: Mail },
                    { key: "phone", label: "Phone Number", placeholder: "+971-50-XXXXXXX", icon: Phone },
                  ].map(field => (
                    <div key={field.key} style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.4)", marginBottom: 6, display: "block" }}>{field.label}</label>
                      <div style={{ position: "relative" }}>
                        <field.icon size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.2)", pointerEvents: "none" }} />
                        <input
                          type="text"
                          placeholder={field.placeholder}
                          value={demoForm[field.key]}
                          onChange={e => setDemoForm(f => ({ ...f, [field.key]: e.target.value }))}
                          style={{
                            width: "100%", padding: "12px 16px 12px 42px", borderRadius: 12, fontSize: 13,
                            border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)",
                            color: "#fff", fontFamily: "inherit", outline: "none", transition: "all 0.2s ease", boxSizing: "border-box",
                          }}
                          onFocus={e => { e.target.style.borderColor = "rgba(14,165,233,0.4)"; e.target.style.background = "rgba(255,255,255,0.06)"; }}
                          onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; e.target.style.background = "rgba(255,255,255,0.04)"; }}
                        />
                      </div>
                    </div>
                  ))}

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.4)", marginBottom: 6, display: "block" }}>Message (optional)</label>
                    <div style={{ position: "relative" }}>
                      <MessageSquare size={15} style={{ position: "absolute", left: 14, top: 14, color: "rgba(255,255,255,0.2)", pointerEvents: "none" }} />
                      <textarea
                        placeholder="Tell us about your needs..."
                        value={demoForm.message}
                        onChange={e => setDemoForm(f => ({ ...f, message: e.target.value }))}
                        rows={3}
                        style={{
                          width: "100%", padding: "12px 16px 12px 42px", borderRadius: 12, fontSize: 13,
                          border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)",
                          color: "#fff", fontFamily: "inherit", outline: "none", transition: "all 0.2s ease",
                          boxSizing: "border-box", resize: "vertical", minHeight: 80,
                        }}
                        onFocus={e => { e.target.style.borderColor = "rgba(14,165,233,0.4)"; e.target.style.background = "rgba(255,255,255,0.06)"; }}
                        onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; e.target.style.background = "rgba(255,255,255,0.04)"; }}
                      />
                    </div>
                  </div>

                  <button onClick={handleDemoSubmit}
                    style={{
                      width: "100%", padding: "14px 0", borderRadius: 12, border: "none", cursor: "pointer",
                      background: "linear-gradient(135deg, #10b981, #059669)",
                      color: "#fff", fontSize: 14.5, fontWeight: 600, fontFamily: "inherit",
                      boxShadow: "0 4px 24px rgba(16,185,129,0.3)",
                      transition: "all 0.2s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.01)"; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
                  >
                    <Send size={16} /> Submit Request
                  </button>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 20, margin: "0 auto 20px",
                    background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Check size={32} style={{ color: "#10b981" }} />
                  </div>
                  <h2 style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Request Submitted!</h2>
                  <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", lineHeight: 1.6, maxWidth: 320, margin: "0 auto 28px" }}>
                    Our team will reach out within 24 hours to schedule your personalized demo.
                  </p>
                  <button onClick={() => { setView("signin"); setDemoSubmitted(false); setDemoForm({ company: "", name: "", email: "", phone: "", message: "" }); }}
                    style={{
                      padding: "12px 28px", borderRadius: 12, border: "1px solid rgba(255,255,255,0.1)",
                      background: "rgba(255,255,255,0.03)", color: "rgba(255,255,255,0.6)",
                      fontSize: 13, fontWeight: 500, fontFamily: "inherit", cursor: "pointer",
                    }}
                  >Back to Sign In</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Responsive + Animations */}
      <style>{`
        @media (max-width: 860px) {
          .apcs-login-brand { display: none !important; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
